import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { BasePostInput } from "@/components/common/BasePostInput";
import { draftRepository } from "@/features/composer-drafts/draftRepository";
import { POST_MENTION_PAGE_SIZE } from "@/components/common/mentionSuggestionLimits";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { ComposerDraftScope } from "@/features/composer-drafts/types";
import { useStudyGroupMentionAvailableUsers } from "@/hooks/useStudyGroupMentionAvailableUsers";
import { getApiErrorStatus } from "@/lib/apiErrors";
import type { User } from "@/lib/humhub-api/types";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

export type FailedPostDraft = {
  id: string;
  content: string;
  files?: File[];
};

const isUnavailableDraftTargetError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return status === 403 || status === 404;
};

interface CreatePostProps {
  studyGroupId?: number | null;
  /** A normalized API channel key, never a display label. */
  channelKey?: string | null;
  topicIds?: Array<string | number>;
  onCreatePost: (message: string, files?: File[]) => Promise<void>;
  compact?: boolean;
  onCreated?: () => void;
  onCancel?: () => void;
  cancelButtonLabel?: string;
  onError?: (error: unknown) => void;
  autoFocus?: boolean;
  searchLikeWhenBlurred?: boolean;
  optimisticCloseOnSubmit?: boolean;
  initialDraft?: FailedPostDraft | null;
  onOptimisticSubmitStart?: (draftId: string) => void;
  onOptimisticSubmitError?: (draft: FailedPostDraft, error: unknown) => void;
  draftScope?: ComposerDraftScope;
}

export const CreatePost: React.FC<CreatePostProps> = ({
  studyGroupId,
  channelKey,
  topicIds,
  onCreatePost,
  compact = false,
  onCreated,
  onCancel,
  cancelButtonLabel,
  onError,
  autoFocus = false,
  searchLikeWhenBlurred = false,
  optimisticCloseOnSubmit = false,
  initialDraft,
  onOptimisticSubmitStart,
  onOptimisticSubmitError,
  draftScope,
}) => {
  const [composerResetKey, setComposerResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftTargetUnavailable, setDraftTargetUnavailable] = useState(false);
  const currentUser = useGlobalUserInfo();
  const derivedDraftScope = useMemo<ComposerDraftScope | undefined>(() => {
    const userId = Number(currentUser?.id);
    const numericStudyGroupId = Number(studyGroupId);
    const normalizedChannelKey = channelKey?.trim();

    if (
      !Number.isSafeInteger(userId) ||
      userId <= 0 ||
      !Number.isSafeInteger(numericStudyGroupId) ||
      numericStudyGroupId <= 0 ||
      !normalizedChannelKey
    ) {
      return undefined;
    }

    return {
      kind: "post",
      userId,
      studyGroupId: numericStudyGroupId,
      channelKey: normalizedChannelKey,
    };
  }, [channelKey, currentUser?.id, studyGroupId]);
  const displayName =
    currentUser?.display_name || currentUser?.account?.username || "";
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const {
    data: rawMentionUsers,
    isLoading: isLoadingMentionUsers,
    searchMentionAvailableUsers,
    mentionScopeKey,
    hasMore: hasMoreMentionUsers,
    isFetchingMore: isFetchingMoreMentionUsers,
    loadMoreError: mentionLoadMoreError,
    fetchNextMentionUsers,
  } = useStudyGroupMentionAvailableUsers(studyGroupId || null, {
    topicIds,
    limit: POST_MENTION_PAGE_SIZE,
  });

  const mentionUsers = useMemo((): User[] => {
    return rawMentionUsers || [];
  }, [rawMentionUsers]);

  const handleMentionSearch = useCallback(
    async (query: string): Promise<User[]> => {
      try {
        const users = await searchMentionAvailableUsers(query);
        return users || [];
      } catch (error) {
        console.error("メンション検索中にエラーが発生しました:", error);
        return [];
      }
    },
    [searchMentionAvailableUsers],
  );

  const handleSubmit = async (content: string, files?: File[]) => {
    if (!content.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      if (optimisticCloseOnSubmit) {
        const draftId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        onOptimisticSubmitStart?.(draftId);
        onCreated?.();
        try {
          await onCreatePost(content, files);
        } catch (error) {
          onOptimisticSubmitError?.(
            {
              id: draftId,
              content,
              files,
            },
            error,
          );
          throw error;
        }
        return;
      }
      await onCreatePost(content, files);
      onCreated?.();
    } catch (error) {
      const activeDraftScope = draftScope ?? derivedDraftScope;
      if (activeDraftScope && isUnavailableDraftTargetError(error)) {
        setDraftTargetUnavailable(true);
        void draftRepository.markTargetUnavailable(activeDraftScope);
      }
      onError?.(error);
      console.error("Failed to create post:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = useCallback(() => {
    setComposerResetKey((current) => current + 1);
    onCancel?.();
  }, [onCancel]);

  return (
    <div className="flex w-full items-center gap-4 rounded-[34px] border border-slate-200 bg-white p-3.5">
      <Avatar className="size-12 shrink-0 overflow-hidden rounded-full">
        <AvatarImage src={currentUser?.image_url || ""} alt={displayName} />
        <AvatarFallback>{initials || "U"}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <BasePostInput
          key={composerResetKey}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          cancelButtonLabel={cancelButtonLabel}
          disabled={isSubmitting}
          mentionUsers={mentionUsers}
          isLoadingMentionUsers={isLoadingMentionUsers}
          hasMoreMentionUsers={hasMoreMentionUsers}
          isFetchingMoreMentionUsers={isFetchingMoreMentionUsers}
          mentionLoadMoreError={mentionLoadMoreError}
          onLoadMoreMentionUsers={fetchNextMentionUsers}
          onMentionTrigger={handleMentionSearch}
          mentionScopeKey={mentionScopeKey}
          compact={compact}
          autoFocus={autoFocus}
          searchLikeWhenBlurred={searchLikeWhenBlurred}
          initialContent={initialDraft?.content}
          initialFiles={initialDraft?.files}
          initialDraftKey={initialDraft?.id}
          draftScope={draftScope ?? derivedDraftScope}
          draftTargetUnavailable={draftTargetUnavailable}
          className="!rounded-[20px] border-2 border-[#2f8587] px-4 py-2"
          containerClassName="!rounded-none !border-0 !bg-transparent !p-0 !shadow-none"
          submitIconOnly
        />
      </div>
    </div>
  );
};
